<script lang="ts" setup>
import type { SizeVariants } from '~kui/types/kui';

import { TabsContent, TabsList, TabsRoot, TabsTrigger } from 'reka-ui';

import KUICoreTransitionFadeIn from '../Transition/KUICoreTransitionFadeIn.vue';

interface Tab {
  value: string;
  label: string;
  /** Show a small dot next to the label to indicate pending state (e.g. unsaved changes). */
  dot?: boolean;
  /** Disables the trigger and shades it grey. */
  disabled?: boolean;
}
interface Props {
  tabs: Tab[];
  position?: 'start' | 'center' | 'end';
  verticallyGrowTabContents?: boolean;
  tabToContentGapSize?: Extract<SizeVariants, 'sm' | 'md' | 'lg'>;
  /**
   * Keep every tab's panel mounted (inactive ones hidden) instead of lazily
   * rendering only the active panel. Use when a consumer needs to validate or
   * read across all panels at once (e.g. a multi-tab form with a single Save).
   */
  mountAll?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  verticallyGrowTabContents: false,
  tabToContentGapSize: 'md',
  position: 'center',
  mountAll: false,
});

const activeTabValue = defineModel<string | undefined>();

if (!activeTabValue.value) {
  activeTabValue.value = props.tabs[0]?.value;
}

// The row scrolls horizontally rather than squashing or wrapping when the labels
// outrun the width. `py-2 -my-2` keeps focus rings from being clipped by the scroll
// container; triggers carry `shrink-0`.
const tabsListClasses = computed(() => {
  return twMerge(
    'justify-center flex w-full space-x-4 overflow-x-auto scrollbar-hidden py-2 -my-2',
    cva('', {
      variants: {
        position: {
          start: 'justify-start',
          center: 'justify-center',
          end: 'justify-end',
        },
      },
    })({
      position: props.position,
    }),
  );
});

const tabTriggerClasses = cva(
  'relative shrink-0 cursor-pointer px-4 pt-2 text-lg font-medium transition-colors hover:bg-accent/50 disabled:cursor-not-allowed',
  {
    variants: {
      state: {
        active: 'border-b-2 pb-2 text-foreground border-foreground',
        inactive: 'border-b pb-[9px] text-muted-foreground border-border',
        disabled: 'border-b pb-[9px] text-muted-foreground border-border',
      },
    },
    defaultVariants: {
      state: 'inactive',
    },
  },
);

const getTabTriggerClass = (tab: Tab) => {
  const state = tab.disabled
    ? 'disabled'
    : activeTabValue.value === tab.value
      ? 'active'
      : 'inactive';
  return twMerge(tabTriggerClasses({ state }));
};

const tabsRootClasses = computed(() => {
  return twMerge(
    cva('', {
      variants: {
        verticallyGrow: {
          true: 'flex h-full min-h-0 flex-1 flex-col',
          false: 'w-full',
        },
      },
    })({
      verticallyGrow: props.verticallyGrowTabContents,
    }),
  );
});

const tabsContentClasses = computed(() => {
  return twMerge(
    cva('', {
      variants: {
        verticallyGrow: {
          true: 'flex h-full min-h-0 flex-1 flex-col',
          false: '',
        },
        gapSize: {
          sm: 'mt-3',
          md: 'mt-6',
          lg: 'mt-12',
        },
      },
    })({
      verticallyGrow: props.verticallyGrowTabContents,
      gapSize: props.tabToContentGapSize,
    }),
  );
});
</script>

<template>
  <TabsRoot v-model="activeTabValue" :class="tabsRootClasses">
    <TabsList v-if="tabs.length > 1" :class="tabsListClasses">
      <TabsTrigger
        v-for="tab in tabs"
        :key="`tab-trigger-${tab.value}`"
        :value="tab.value"
        :disabled="tab.disabled"
        :class="getTabTriggerClass(tab)"
      >
        <span class="inline-flex items-center gap-1.5">
          {{ tab.label }}
          <span
            v-if="tab.dot"
            class="inline-block h-1.5 w-1.5 rounded-full bg-brand-orange"
            data-testid="tab-dot"
          />
        </span>
      </TabsTrigger>
    </TabsList>
    <!-- Keep all panels mounted (reka-ui force-mount renders them all, so we hide
         the inactive ones ourselves) for cross-panel reads/validation. -->
    <template v-if="mountAll">
      <TabsContent
        v-for="tab in tabs"
        :key="`tab-content-${tab.value}`"
        :value="tab.value"
        force-mount
        :class="[tabsContentClasses, { hidden: tab.value !== activeTabValue }]"
      >
        <slot :name="tab.value" />
      </TabsContent>
    </template>
    <KUICoreTransitionFadeIn v-else>
      <TabsContent
        :key="activeTabValue"
        :value="activeTabValue!"
        :class="tabsContentClasses"
      >
        <slot :name="activeTabValue" />
      </TabsContent>
    </KUICoreTransitionFadeIn>
  </TabsRoot>
</template>
